import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import { useFileManagerContext } from 'modules/FileManagerV2/context/FileManagerContext';
import { presignedURLGet } from 'modules/FileManagerV2/api/services';

import CustomHeader from '../file/CustomHeader';
import { useToast } from 'context/ToastContext';
import ViewerRouter from '../preview/ViewerRouter';

import { uploadFilesV2 } from 'utils/filesUtils';
import { useForm } from 'react-hook-form';
import CourtEventForm from './CourtEventForm';
import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { UserDetailsContext } from 'context/userDetailsContext';
import { addEvent } from 'services/Calender/calendarServices';
import { toastConstant } from 'constants/toastmessage';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import moment from 'moment-timezone';
import { getTenantDetails, parseRemiderPayload } from 'utils/utils';
import { useLoaderDispatch } from 'context/LoaderContext';
import { loadLocations } from 'modules/settings/workflow_automation/submodules/events/helpers/createEventHelpers';
import { useActivityLogPreview } from 'hooks/useActivityLogPreview';
import { uuidv4 } from 'common/uuidGenerator';

function CreateCourtEventModal() {
  const {
    modals,
    selectedItem,
    isCaseFileManager,
    isLeadFileManager,
    isGlobalFileManager,
    isClientFileManager,
    caseDetails,
    hideCreateEventModal,
    courtDateMsg,
    onMountUnMount,
  } = useFileManagerContext();
  const websocketContext = useContext(WebSocketConnectionContext);
  const userContext = useContext(UserDetailsContext);
  const { showCreateEventModal, setShowCreateEventModal } = modals;
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [courtEventExtractedData, setCourtEventExtractedData] = useState([]);
  const [isLoading, setIsLoading] = useState([]);
  const [locationsList, setLocationsList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isResolvingPreviewUrl, setIsResolvingPreviewUrl] = useState(false);
  const { addToast } = useToast();
  const { addActivity } = useActivityLogPreview();
  const lastProcessedMessageRef = useRef(null);

  // Function to handle closing the modal and resetting state
  const handleCloseModal = () => {
    setShowCreateEventModal(false);
    setIsLoading(false);
    setCourtEventExtractedData([]);
    // Clear the websocket data so it doesn't show old toasts when reopening
    websocketContext?.setAiCourtDate?.(null);
  };

  const userId = userContext?.firmDetails?.current_user;
  const setLoader = useLoaderDispatch();
  const {
    handleSubmit,
    control,
    resetField,
    setValue,
    getValues,
    watch,
    formState: { errors },
    trigger,
  } = useForm();

  useEffect(() => {
    if (!showCreateEventModal || !selectedItem) {
      setPreviewUrl(null);
      setIsResolvingPreviewUrl(false);
      return undefined;
    }

    if (selectedItem.url) {
      setPreviewUrl(selectedItem.url);
      setIsResolvingPreviewUrl(false);
      return undefined;
    }

    const filePath = selectedItem.file_path;
    if (!filePath || filePath.startsWith('public/')) {
      setPreviewUrl(null);
      setIsResolvingPreviewUrl(false);
      return undefined;
    }

    let cancelled = false;
    setIsResolvingPreviewUrl(true);

    presignedURLGet(filePath, selectedItem.version_id, false, true, selectedItem.sk, selectedItem.pk)
      .then((res) => {
        if (!cancelled) setPreviewUrl(res?.data?.file_url || null);
      })
      .catch((err) => {
        console.error('Failed to resolve presigned URL:', err);
        if (!cancelled) setPreviewUrl(null);
      })
      .finally(() => {
        if (!cancelled) setIsResolvingPreviewUrl(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showCreateEventModal, selectedItem]);

  const previewItem = useMemo(() => {
    if (!selectedItem) return null;
    const url = previewUrl || selectedItem.url || null;
    return url === selectedItem.url ? selectedItem : { ...selectedItem, url };
  }, [selectedItem, previewUrl]);

  const saveCreateEvent = async (data) => {
    if (data?.attachments?.length > 0) {
      setLoader(true);

      try {
        const res = await uploadFilesV2(data?.attachments, {
          module: 'calendar',
          caseId: selectedItem?.case_id || undefined,
          userId: userId || undefined,
          isTemp: true,
          tempPathId: uuidv4(),
        });
        data.attachments = res;
      } catch (err) {
        console.log(err);
      } finally {
        setLoader(false);
      }
    }

    data.reminder = parseRemiderPayload(data.reminder);
    data.reminder = Array.from(new Set(data?.reminder));

    const timezone = userContext?.userDetails?.timezone || moment.tz.guess();

    // Combine the date from 'when' with the times from the time pickers
    // Use moment.tz to interpret the values as being in the user's timezone
    const eventDate = new Date(data.when);

    const year = eventDate.getFullYear();
    const month = eventDate.getMonth();
    const date = eventDate.getDate();

    if (data?.meeting_start_time) {
      const startTime = new Date(data?.meeting_start_time);
      // Create moment in user's timezone with the exact date and time values selected
      data.meeting_start_time = moment
        .tz({ year, month, date, hour: startTime.getHours(), minute: startTime.getMinutes(), second: 0 }, timezone)
        .format();
    }

    if (data?.meeting_end_time) {
      const endTime = new Date(data.meeting_end_time);
      // Create moment in user's timezone with the exact date and time values selected
      data.meeting_end_time = moment
        .tz({ year, month, date, hour: endTime.getHours(), minute: endTime.getMinutes(), second: 0 }, timezone)
        .format();
    }

    // For 'when', interpret the selected date as being in the user's timezone
    data.when = moment.tz({ year, month, date, hour: 0, minute: 0, second: 0 }, timezone).format();

    data.case_name = isCaseFileManager || isLeadFileManager ? caseDetails?.case_description?.case_name : data.case?.case_name ?? '';
    data.case_id = isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : selectedItem?.case_id;
    data.is_lead = isLeadFileManager ? isLeadFileManager : data?.case?.is_lead;

    data.icon = data.event_type?.event_type_icon ?? '';

    const normalizeToArray = (value) => {
      if (!value) return undefined;
      return Array.isArray(value) ? value : [value];
    };

    let practiceArea;

    if (isCaseFileManager || isLeadFileManager) {
      practiceArea =
        caseDetails?.case?.practice_area ??
        caseDetails?.case_description?.case_practice_area ??
        data?.case?.practice_area ??
        data?.practice_area;
    } else {
      practiceArea = data?.case?.practice_area ?? data?.practice_area;
    }

    const normalized = normalizeToArray(practiceArea);

    if (normalized) {
      data.practice_area = normalized;
    }

    data.guests = [...data.contacts, ...data.firm_users];

    if (isCaseFileManager || isLeadFileManager) {
      const { case_id, case_description } = caseDetails;
      const { case_name, unique_number } = case_description;

      data.case = {
        id: case_id,
        value: case_name,
        label: case_name ? `${unique_number ? `${unique_number} ` : ''}${case_name}` : 'TBD',
      };
    }

    data.recurrence_keys = data.recurrence_keys ?? {};

    if (data.is_recurring) {
      data.recurrence_keys.recurrence_limit = convertToTimezoneFormat(
        data.recurrence_keys.recurrence_limit,
        userContext?.userDetails?.timezone
      );
    }

    try {
      await addEvent({
        data,
        case_id: isCaseFileManager || isLeadFileManager ? caseDetails?.case_id : selectedItem?.case_id,
        is_lead: isLeadFileManager ? isLeadFileManager : selectedItem?.is_lead,
      });
      addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.EVENT_CREATED_SUCCESS);
      setUploadedAttachments();
      setCourtEventExtractedData();
      hideCreateEventModal();
    } catch (err) {
      console.log(err);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.EVENT_CREATE_FAILURE);
    }
  };

  const hadleAddNewLocation = (data) => {
    setLocationsList((prev) => [...prev, { label: data.title, value: data.title }]);
  };

  useEffect(() => {
    const courtDateMessage = websocketContext?.aiCourtDate?.body?.court_date_message;
    if (!courtDateMessage?.details) return;
    // Create a unique identifier for this message to prevent duplicate toasts
    const { message, isError } = courtDateMessage;
    const messageId = JSON.stringify({ message, isError });

    // Only process if this is a new message we haven't seen
    if (lastProcessedMessageRef.current === messageId) return;
    setIsLoading(false);
    lastProcessedMessageRef.current = messageId;

    if (isError && message) {
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, message);
    }
    setCourtEventExtractedData(courtDateMessage);
  }, [websocketContext?.aiCourtDate, addToast]);

  useEffect(() => {
    setIsLoading(true);
  }, [courtDateMsg]);

  useEffect(() => {
    loadLocations(setLocationsList);
  }, []);

  const handleUploadAttachements = (items) => {
    setUploadedAttachments(items);
  };

  const createActivityLog = (item) => {
    setTimeout(() => {
      const [tenantId] = getTenantDetails();
      const data = {
        event_key: 'Read',
        event_type: 'document',
        event_subtype: 'read_document',
        message: `Viewed Document: ${item?.display_name}`,
        event_status: 'success',
        pk: item?.pk,
        sk: item?.sk,
        event_items: {
          case_id: item?.case_id || '',
          tenant_id: tenantId,
          case_name: item?.case_name || '',
          is_lead: item?.is_lead,
        },
      };
      addActivity(data);
    }, 5000);
    //Automated time entry logic
    isCaseFileManager && typeof onMountUnMount === 'function' && onMountUnMount('file-preview', 'mounted', caseDetails?.case_id);
  };

  const renderLoader = (data) => {
    return (
      <div className="overlay d-flex flex-column p-1 align-items-center">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '3em' }}></i>
        <div className="mt-3">{data?.message}</div>
      </div>
    );
  };

  return (
    <Dialog
      className="my-dialog custom-dialog preview-dialog court-setting-modal"
      header={<CustomHeader onHide={handleCloseModal} />}
      visible={showCreateEventModal}
      modal
      onHide={handleCloseModal}
      closable={false}
    >
      <div className="row">
        <div className="d-flex mb-2 align-items-center mt-1">
          <span className="d-flex p-0 black-700" style={{ lineHeight: '18px', fontSize: '12px' }}>
            <i className="pi pi-info-circle me-2"></i>
            AI-generated responses may contain errors or inaccuracies. You should not rely on AI responses without independent verification.
          </span>
        </div>

        <div className="col-lg-8 col-md-6 col-12 court-settings-preview d-flex flex-column" style={{ maxHeight: '100%' }}>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
            <div className="modal-heading F-size16 text-bold">{`Preview of ${previewItem?.file_name || selectedItem?.file_name}`}</div>
          </div>
          <div className="court-settings-preview__viewer">
            {isResolvingPreviewUrl && !previewItem?.url
              ? renderLoader({ message: 'Loading preview...' })
              : previewItem && (
                  <ViewerRouter
                    key={previewItem.object_id}
                    item={previewItem}
                    isClientFileManager={isClientFileManager}
                    onLoad={() => createActivityLog(previewItem)}
                  />
                )}
          </div>
        </div>

        {isLoading ? (
          <div className="col-lg-4 col-md-6 col-12 mb-1 d-flex align-items-center justify-content-center" style={{ overflow: 'auto' }}>
            {renderLoader(courtDateMsg)}
          </div>
        ) : (
          <div className="col-lg-4 col-md-6 col-12 mb-1 court-settings-form">
            <CourtEventForm
              saveCreateEvent={saveCreateEvent}
              hideCreateEventModal={hideCreateEventModal}
              selectedItem={selectedItem}
              control={control}
              resetField={resetField}
              setValue={setValue}
              getValues={getValues}
              watch={watch}
              errors={errors}
              trigger={trigger}
              handleUploadAttachements={handleUploadAttachements}
              uploadedAttachments={uploadedAttachments}
              courtEventExtractedData={courtEventExtractedData}
              locationsList={locationsList}
              hadleAddNewLocation={hadleAddNewLocation}
            />
            <div className="footer-btn">
              <Button className="p-button-secondary outline mx-2" label="Cancel" onClick={handleCloseModal} />
              <Button className="p-button p-button-secondary" label="Save" onClick={handleSubmit(saveCreateEvent)} />
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default CreateCourtEventModal;
